import { Card } from 'components/Card';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL_BACK,
  REMOTE_ASESETUP_SECTIONDELETE,
  REMOTE_ASESETUP_SECTIONSAVE,
  REMOTE_ASESETUP_SECTIONCLOSED,
} from 'store/actions';
import {
  selectAseSetupLibraryFolder,
  selectAseSetupLibrarySection,
  selectAseSetupSectionId,
} from 'store/remote/selectors';
import { AssessmentLibrary } from 'store/remote/types';
import { useAlert } from 'hooks/useAlert';
import { Button } from 'components/Button';
import { PageHeader } from 'components/PageHeader';

export const SectionScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYSECTION>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const alert = useAlert();
  const forceUpdate = useForceUpdate();

  const { folderId } = route.params;

  const sectionId = useSelector(selectAseSetupSectionId);
  const initIdRef = React.useRef(sectionId);

  const section: AssessmentLibrary['sections'][string] = useSelector((state) =>
    selectAseSetupLibrarySection(state, sectionId || -1),
  ) || {
    id: 0,
    name: '',
    description: '',
    reportDescription: '',
    folderId: folderId || -1,
    ordering: 0,
  };

  const folder = useSelector((state) =>
    selectAseSetupLibraryFolder(state, section.folderId),
  );

  const isDirty = React.useRef(false);
  const data = React.useRef(cloneDeep(section));
  const checkIsDirty = () => !isEqual(section, data.current);

  // Reset data if ID changes, then check isDirty
  if (data.current.id !== section.id) {
    data.current = cloneDeep(section);
  }
  isDirty.current = checkIsDirty();

  const shouldReload = () => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  };

  const handleChangeWithName = <T extends keyof typeof data.current>(
    name: T,
    value: (typeof data.current)[T],
  ) => {
    data.current = {
      ...data.current,
      [name]: value,
    };
    shouldReload();
  };

  const handleSavePress = () => {
    if (!data.current.name.trim()) {
      alert({
        text: 'Please enter a name',
      });
      return;
    }
    if (isDirty.current) {
      dispatch(
        REMOTE_ASESETUP_SECTIONSAVE({
          id: sectionId,
          data: data.current,
          addData: {
            folderId,
          },
        }),
      );
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSavePress,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleDelete = () =>
    dispatch(REMOTE_ASESETUP_SECTIONDELETE({ id: section.id }));

  React.useEffect(() => {
    if (initIdRef.current && !sectionId) {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  }, [dispatch, sectionId]);

  React.useEffect(() => {
    return () => {
      dispatch(REMOTE_ASESETUP_SECTIONCLOSED());
    };
  }, [dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={sectionId ? 'Section' : 'New Section'}
          subtitle={folder ? `Folder: ${folder.name}` : ''}
          onPressCancelButton={handleClose}
          onPressSaveButton={
            sectionId || isDirty.current ? handleSavePress : null
          }
        />
      }
    >
      <Card
        label="Name"
        explanation="This description is shown to assessors but doesn't appear on reports"
      >
        <TextAreaField
          name="name"
          onChangeWithName={handleChangeWithName}
          value={data.current.name}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusNative={!data.current.id}
          autoFocusDelay
        />
      </Card>

      <Card
        label="Description"
        explanation="This description is shown to assessors but doesn't appear on reports"
      >
        <TextAreaField
          name="description"
          onChangeWithName={handleChangeWithName}
          value={data.current.description}
          onEsc={handleClose}
        />
      </Card>

      <Card
        label="Report Description"
        explanation="This report description is not shown to assessors but does appear on reports"
      >
        <TextAreaField
          name="reportDescription"
          onChangeWithName={handleChangeWithName}
          value={data.current.reportDescription}
          onEsc={handleClose}
        />
      </Card>
      {!!section.id && (
        <Button
          text="Delete Section"
          destructive
          onPress={handleDelete}
          style={{ alignSelf: 'center' }}
        />
      )}
    </Page>
  );
};
